import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouterHistory,
} from "vue-router";

import { routeComponentLoaders } from "@/router/routePreload";
import { installAdminAccessGuard } from "@/router/adminAccess";
import { useUserStore } from "@/stores/user";

export const appRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: { name: "release1-promotion" },
  },
  {
    path: "/promo/release1/:pathMatch(.*)*",
    name: "release1-promotion",
    component: routeComponentLoaders.promotion,
    meta: {
      immersive: true,
      requiresAuth: false,
      title: "ApplyKitty Release 1",
    },
  },
  {
    path: "/",
    name: "app-layout",
    component: routeComponentLoaders["app-layout"],
    meta: { requiresAuth: false, title: "ApplyKitty" },
    children: [
      {
        path: "profile",
        name: "profile",
        component: routeComponentLoaders.profile,
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Profile",
          navigationLabel: "Profile",
          sidebarDescription: "Profile & CV setup",
        },
      },
      {
        path: "roles",
        name: "roles",
        component: routeComponentLoaders.roles,
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Roles",
          navigationLabel: "Roles",
          sidebarDescription: "Role discovery",
        },
      },
      {
        path: "tracking",
        name: "tracking",
        component: routeComponentLoaders.tracking,
        meta: {
          requiresAuth: true,
          title: "Tracking",
          navigationLabel: "Tracking",
          sidebarDescription: "Application tracking",
        },
      },
      {
        path: "mentors/matches",
        name: "mentor-matching",
        component: routeComponentLoaders["mentor-matching"],
        meta: { requiresAuth: true, title: "Mentor matching" },
      },
      {
        path: "mentors/:mentorId/availability",
        name: "mentor-availability",
        component: routeComponentLoaders["mentor-availability"],
        meta: { requiresAuth: true, title: "Mentor availability" },
      },
      {
        path: "mentors/bookings",
        name: "mentor-sessions",
        component: routeComponentLoaders["mentor-sessions"],
        meta: {
          requiresAuth: true,
          title: "Mentor Sessions",
          navigationLabel: "Mentor Sessions",
          sidebarDescription: "Mentor session bookings",
        },
      },
      {
        path: "mentors/bookings/:bookingId",
        name: "mentor-booking",
        component: routeComponentLoaders["mentor-booking"],
        meta: { requiresAuth: true, title: "Mentor booking" },
      },
      {
        path: "support/tip-return",
        name: "support-tip-return",
        component: routeComponentLoaders["support-tip-return"],
        meta: { requiresAuth: true, title: "Support payment" },
      },
      {
        path: "account",
        name: "account",
        component: routeComponentLoaders.account,
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Account",
          sidebarDescription: "Account settings",
        },
      },
      {
        path: "account/manage-plan",
        name: "manage-plan",
        component: routeComponentLoaders["manage-plan"],
        meta: {
          requiresAuth: true,
          title: "Manage plan",
          sidebarDescription: "Manage subscription plan",
        },
      },
      {
        path: "account/compare-plans",
        name: "compare-plans",
        component: routeComponentLoaders["compare-plans"],
        meta: {
          requiresAuth: true,
          title: "Compare plans",
          sidebarDescription: "Compare subscription plans",
        },
      },
      {
        path: "review",
        name: "review",
        component: routeComponentLoaders.review,
        meta: {
          requiresAuth: true,
          title: "Review",
          navigationLabel: "Review",
          sidebarDescription: "Application review",
        },
      },
      {
        path: "history",
        name: "history",
        component: routeComponentLoaders.history,
        meta: {
          requiresAuth: true,
          title: "History",
          navigationLabel: "History",
          sidebarDescription: "Application history",
        },
      },
      {
        path: "inbox",
        name: "inbox",
        component: routeComponentLoaders.inbox,
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Inbox",
          navigationLabel: "Inbox",
          sidebarDescription: "Application inbox",
        },
      },
      {
        path: "company-wishlist",
        name: "company-wishlist",
        component: routeComponentLoaders["company-wishlist"],
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Company Wishlist",
          navigationLabel: "Company Wishlist",
          sidebarDescription: "Company shortlist",
        },
      },
      {
        path: "skilled-worker-list",
        name: "skilled-worker-list",
        component: routeComponentLoaders["skilled-worker-list"],
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "Skilled Worker List",
          navigationLabel: "Skilled Worker List",
          sidebarDescription: "UK sponsor directory",
        },
      },
      {
        path: "documents",
        name: "documents",
        component: routeComponentLoaders.documents,
        meta: {
          alignContentBottom: true,
          requiresAuth: true,
          title: "CV & Cover Letter",
          navigationLabel: "CV & Cover Letter",
          sidebarDescription: "CV & cover letter builder",
        },
      },
      {
        path: "documents/:packId/print",
        name: "document-print",
        component: routeComponentLoaders["document-print"],
        meta: {
          hideSidebar: true,
          printWorkspace: true,
          requiresAuth: true,
          title: "Print workspace",
          sidebarDescription: "Document print workspace",
        },
      },
      {
        path: "documents/:packId",
        name: "document-editor",
        component: routeComponentLoaders["document-editor"],
        meta: {
          hideSidebar: true,
          requiresAuth: true,
          title: "Document Editor",
          sidebarDescription: "Document editor",
        },
      },
      {
        path: "admin",
        name: "admin-overview",
        component: routeComponentLoaders["admin-overview"],
        meta: {
          requiredCapability: "admin:read",
          requiresAuth: true,
          title: "Administration",
          navigationLabel: "Admin",
          sidebarDescription: "Administration overview",
        },
      },
      {
        path: "admin/users",
        name: "admin-users",
        component: routeComponentLoaders["admin-users"],
        meta: {
          requiredCapability: "admin:read",
          requiresAuth: true,
          title: "Admin users",
          sidebarDescription: "Administration users",
        },
      },
      {
        path: "admin/applications",
        name: "admin-applications",
        component: routeComponentLoaders["admin-applications"],
        meta: {
          requiredCapability: "admin:read",
          requiresAuth: true,
          title: "Admin applications",
          sidebarDescription: "Administration applications",
        },
      },
      {
        path: "admin/support",
        name: "admin-support",
        component: routeComponentLoaders["admin-support"],
        meta: {
          requiredCapability: "admin:read",
          requiresAuth: true,
          title: "Admin support",
          sidebarDescription: "Administration support",
        },
      },
      {
        path: "admin/commercial",
        name: "admin-commercial",
        component: routeComponentLoaders["admin-commercial"],
        meta: {
          requiredCapability: "admin:read",
          requiresAuth: true,
          title: "Admin commercial",
          sidebarDescription: "Administration commercial",
        },
      },
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        component: routeComponentLoaders["not-found"],
        meta: {
          requiresAuth: false,
          title: "Page not found",
          sidebarDescription: "Page not found",
        },
      },
    ],
  },
];

export function createAppRouter(history: RouterHistory = createMemoryHistory()) {
  const appRouter = createRouter({
    history,
    routes: appRoutes,
    scrollBehavior(to, _from, savedPosition) {
      if (savedPosition) return savedPosition;
      if (to.name === "account" && to.hash) return { left: 0, top: 0 };
      if (to.hash) return { el: to.hash };
      return { left: 0, top: 0 };
    },
  });
  appRouter.afterEach((to) => {
    document.title = to.meta.title ? `${to.meta.title} | ApplyKitty` : "ApplyKitty";
  });
  installAdminAccessGuard(appRouter, () => useUserStore());
  return appRouter;
}

export const router = createAppRouter(createWebHistory());
